import React, { useState, useEffect } from "react";
import MaterialListSelect from "./MaterialListSelect";
import MaterialLocationSelect from "./MaterialLocationSelect";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ConfirmDialog from "./ConfirmDialog";
import LoadingOverlay from "./LoadingOverlay";

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400";
const rdOnly =
  "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-100 text-gray-500 cursor-not-allowed";

const STOCK_TYPES = ["PLEASE SELECT", "Stock In", "Stock Out"];

function StockInOutModal({ dataSet, onClose, refreshData }) {
  const { user } = useSelector((state) => state.auth);
  const [selectedMatList, setSelectedMatList] = useState(null);
  const [productLocation, setProductLocation] = useState();
  const [currStock, setCurrStock] = useState(null);
  const [stockTypes, setStockType] = useState(STOCK_TYPES[0]);
  const [stockQty, setStockQty] = useState("");
  const [remark, setRemark] = useState("");
  const [transFor, setTransFor] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

  useEffect(() => {
    if (dataSet) {
      setStockQty(
        dataSet.transaction_type === "Stock Out"
          ? dataSet.stock_out
          : dataSet.stock_in,
      );
      setSelectedMatList(dataSet.product_id);
      setRemark(dataSet.transaction_remark || "");
      setTransFor(dataSet.transaction_for || "");
      setStockType(dataSet.transaction_type);
    }
  }, [dataSet]);

  const handleMatListChange = (Arr) => {
    setSelectedMatList(Arr[0]);
    getCurrentStock(Arr[0]);
  };

  const handleQtyChange = (e) => {
    const v = e.target.value;
    if (/^[0-9]*$/.test(v)) setStockQty(v);
  };

  const getCurrentStock = async (product_id) => {
    if (!product_id) {
      setCurrStock(null);
      return;
    }
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/currentallstock/${product_id}`,
      );
      if (res.status === 200) setCurrStock(res.data.result);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to fetch stock.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (stockTypes === "Stock Out") {
      const available =
        (currStock?.current_stock ?? 0) + (dataSet?.stock_out ?? 0);
      if (Number(stockQty) > available) {
        toast.error("Stock Out qty exceeds available stock.");
        return;
      }
    }
    setShowUpdateConfirm(true);
  };

  const updateTransRecord = async () => {
    setIsLoading(true);
    try {
      await axios.patch(
        "https://eps.elkomprintama.com/patch/api/store/transaction/update",
        {
          stockIn: stockTypes === "Stock In" ? stockQty : 0,
          stockOut: stockTypes === "Stock Out" ? stockQty : 0,
          transactionType: stockTypes,
          transactionBy: user.username,
          transactionFor: transFor,
          transactionRemark: remark,
          transactionId: dataSet.transaction_id,
        },
      );
      toast.success("Transaction updated.");
      onClose();
      refreshData();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Update failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const voidTransactions = async () => {
    setIsLoading(true);
    try {
      await axios.patch(
        "https://eps.elkomprintama.com/patch/api/store/transaction/update",
        {
          stockIn: 0,
          stockOut: 0,
          transactionType: stockTypes,
          transactionBy: user.username,
          transactionFor: transFor,
          transactionRemark: "[VOID] " + remark,
          transactionId: dataSet.transaction_id,
        },
      );
      toast.success("Transaction voided.");
      onClose();
      refreshData();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Void failed.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg relative">
      {isLoading && <LoadingOverlay message="Processing…" />}

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-sm font-bold text-gray-800">Edit Transaction</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            ID: {dataSet?.transaction_id}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <MaterialLocationSelect
          dataSet={dataSet}
          selectedLocation={productLocation}
          onChangeList={setProductLocation}
          locked={true}
        />

        <MaterialListSelect
          dataSet={dataSet}
          onChangeList={handleMatListChange}
          location_id={productLocation}
          locked={true}
        />

        {/* Stock type */}
        <div>
          <label className={lbl}>Stock Type</label>
          <select
            className={inp}
            value={stockTypes}
            onChange={(e) => setStockType(e.target.value)}
          >
            {STOCK_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {/* QTY + Stock Left */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={lbl}>QTY</label>
            <input
              className={inp}
              type="text"
              placeholder="QTY"
              value={stockQty}
              onChange={handleQtyChange}
            />
          </div>
          <div>
            <label className={lbl}>Stock Left</label>
            <input
              className={rdOnly}
              type="text"
              disabled
              readOnly
              value={
                currStock ? `${currStock.current_stock} ${currStock.unit}` : "—"
              }
            />
          </div>
        </div>

        {/* Remark */}
        <div>
          <label className={lbl}>Remark / Operator</label>
          <input
            className={inp}
            type="text"
            placeholder="Remark / Operator"
            value={remark}
            onChange={(e) => setRemark(e.target.value.toUpperCase())}
          />
        </div>

        {/* Customer */}
        <div>
          <label className={lbl}>Customer</label>
          <input
            className={inp}
            type="text"
            placeholder="Customer"
            value={transFor}
            onChange={(e) => setTransFor(e.target.value.toUpperCase())}
          />
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 transition"
          >
            Update
          </button>
          <button
            type="button"
            onClick={() => setShowVoidConfirm(true)}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition"
          >
            Void
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
          >
            Cancel
          </button>
        </div>
      </form>

      {showUpdateConfirm && (
        <ConfirmDialog
          message="Update this transaction?"
          onConfirm={() => {
            setShowUpdateConfirm(false);
            updateTransRecord();
          }}
          onCancel={() => setShowUpdateConfirm(false)}
        />
      )}

      {showVoidConfirm && (
        <ConfirmDialog
          message="VOID this transaction? This cannot be undone."
          onConfirm={() => {
            setShowVoidConfirm(false);
            voidTransactions();
          }}
          onCancel={() => setShowVoidConfirm(false)}
        />
      )}
    </div>
  );
}

export default StockInOutModal;
